import { Center, Loader, Stack } from "@mantine/core";
import { lazy, Suspense } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router";

import { atLeast, AuthProvider, useAuth } from "./auth";
import { LogoMark } from "./components/Logo";
import { PageSkeleton } from "./components/Skeletons";
import { Overview } from "./pages/overview/Overview";
import AdminPage from "./pages/admin/AdminPage";
import EventsPage from "./pages/events/EventsPage";
import OperationsPage from "./pages/operations/OperationsPage";
import ReportsPage from "./pages/reports/ReportsPage";
import RunsPage from "./pages/runs/RunsPage";
import { Login } from "./shell/Login";
import { Shell } from "./shell/Shell";

// three.js loads with the 3D view only (the entry chunk stays small)
const DemPage = lazy(() => import("./pages/dem/DemPage"));
// ECharts likewise loads with the trends only
const TrendsPage = lazy(() => import("./pages/trends/TrendsPage"));

export function Routed() {
  const { me, loading } = useAuth();
  if (loading)
    return (
      <Center h="100vh">
        <Stack align="center" gap="md">
          <LogoMark size={44} />
          <Loader size="sm" type="dots" />
        </Stack>
      </Center>
    );
  if (me === null) return <Login />;
  return (
    <Routes>
      <Route element={<Shell />}>
        <Route index element={<Overview />} />
        <Route
          path="dem"
          element={
            <Suspense fallback={<PageSkeleton />}>
              <DemPage />
            </Suspense>
          }
        />
        <Route
          path="trends"
          element={
            <Suspense fallback={<PageSkeleton />}>
              <TrendsPage />
            </Suspense>
          }
        />
        <Route path="runs" element={<RunsPage />} />
        <Route path="reports" element={<ReportsPage />} />
        <Route path="events" element={<EventsPage />} />
        {atLeast(me.role, "operator") && (
          <Route path="operations" element={<OperationsPage />} />
        )}
        {atLeast(me.role, "admin") && (
          <Route path="admin" element={<AdminPage />} />
        )}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}

export function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Routed />
      </AuthProvider>
    </BrowserRouter>
  );
}
